import { useTranslation } from "react-i18next";
import React from "react";
import {
  DealQuestionnaireQuestionSettings,
  QuestionResponse,
  Responses,
  Score
} from "@/pages/deals/types";
import { TextVariant } from "@/components/ui/text";
import { cn } from "@/utils/utils";
import {
  useDeleteQuestionMutation,
  useDeleteQuestionResponseMutation
} from "@/pages/deals/hooks";
import { PersonData } from "@/entities/people/types";
import BaseQuestion from "@/components/controls/Questions/BaseQuestion";
import YesNoAnswer from "@/components/controls/Questions/YesNoAnswer";

interface Props {
  question: DealQuestionnaireQuestionSettings;
  questionnaireId: string;
  dealId: string;
  handleUpdateQuestionnaireScore: (newValue: Score | null) => void;
  localResponses: Responses;
  updateResponse: (
    questionId: string,
    response: QuestionResponse | null
  ) => void;
  onResponseError: () => void;
  options?: string[];
  className?: string;
  hideMandatorySign?: boolean;
  hideTooltip?: boolean;
  relatedQuestion?: DealQuestionnaireQuestionSettings & {
    person: PersonData | null;
  };
  enableDelete?: boolean;
  updateQuestionsState?: (questionIdToDelete: string) => void;
  resetQuestionnaire?: () => Promise<void>;
  questionClassName?: string;
  questionVariant?: TextVariant;
  disabled?: boolean;
}

const YesOrNoQuestion: React.FC<Props> = ({
  question,
  questionnaireId,
  dealId,
  handleUpdateQuestionnaireScore,
  localResponses,
  updateResponse,
  onResponseError,
  options,
  className,
  hideMandatorySign = false,
  hideTooltip = false,
  relatedQuestion,
  enableDelete = false,
  updateQuestionsState,
  resetQuestionnaire,
  questionClassName,
  questionVariant,
  disabled = false
}) => {
  const { t } = useTranslation();
  const deleteQuestionResponse = useDeleteQuestionResponseMutation();
  const deleteQuestion = useDeleteQuestionMutation();

  const handleDelete = () => {
    deleteQuestionResponse.mutate(
      {
        pathParams: { dealId, questionnaireId, questionId: question.id }
      },
      {
        onSuccess: (data) => {
          handleUpdateQuestionnaireScore(data.score);
          deleteQuestion.mutate(
            {
              pathParams: {
                questionId: question.id
              },
              queryParams: {}
            },
            {
              onSuccess: async () => {
                updateQuestionsState?.(question.id);
                await resetQuestionnaire?.();
              }
            }
          );
        },
        onError: onResponseError
      }
    );
  };

  return (
    <BaseQuestion
      question={question}
      questionnaireId={questionnaireId}
      dealId={dealId}
      handleUpdateQuestionnaireScore={handleUpdateQuestionnaireScore}
      localResponses={localResponses}
      updateResponse={updateResponse}
      onResponseError={onResponseError}
      className={cn(className, enableDelete && "group")}
      hideMandatorySign={hideMandatorySign}
      hideTooltip={hideTooltip}
      relatedQuestion={relatedQuestion}
      questionClassName={questionClassName}
      questionVariant={questionVariant}
    >
      <YesNoAnswer
        options={options ?? [t("deals.dealView.yes"), t("deals.dealView.no")]}
        disabled={disabled}
        enableDelete={enableDelete}
        onDelete={handleDelete}
      />
    </BaseQuestion>
  );
};

export default YesOrNoQuestion;
